/**
 * Phase 2 · Admin Quote Adjustments panel.
 *
 * Wraps the four endpoints in `routers/quotes_admin_adjustments.py`:
 *   PATCH /api/admin/quotes/{id}/adjustments        — save discount + custom items
 *   POST  /api/admin/quotes/{id}/finalize           — freeze snapshot
 *   POST  /api/admin/quotes/{id}/reopen             — unfreeze
 *   POST  /api/admin/quotes/{id}/adjustments/convert — atomic idempotent convert
 *
 * Client-side math for the live preview MIRRORS
 * `order_services/quote_adjustments.compute_revised_totals` (money
 * rounded to 2 dp, tax rate is authoritative from the frozen snapshot,
 * discount capped at merch subtotal, custom items split by `taxable`).
 * Save always re-computes on the backend, so any drift is corrected.
 */
import React, { useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Lock, Unlock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { computeRevisedTotals } from "@/lib/quoteAdjustmentsMath";


// re-export for existing test imports (Phase 2 · client-side math parity)
export { computeRevisedTotals };

// ─── local formatting ────────────────────────────────────────────
const money = (x) => Math.round(Number(x || 0) * 100) / 100;

const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;


export default function QuoteAdjustmentsPanel({ quote, onChanged }) {
  const snapshot = useMemo(() => quote?.quote_snapshot || {}, [quote?.quote_snapshot]);
  const merchSubtotal = Number(snapshot?.totals?.subtotal ?? 0);
  const taxRate = Number(snapshot?.totals?.tax?.rate ?? 0) || 0;

  const finalization = quote?.finalization || null;
  const isFinalized = Boolean(finalization?.finalized_at);
  const convertedOrderId = quote?.converted_order_public_id || null;
  const isConverted = Boolean(convertedOrderId);
  const editable = !isFinalized && !isConverted;

  // When frozen, always show the frozen finalized snapshot. When
  // editable, seed from the persisted `adjustments` (if any) or an
  // empty state.
  const source = isFinalized
    ? (finalization?.snapshot || quote?.adjustments || {})
    : (quote?.adjustments || {});
  const seedDiscount = source.discount || { type: "none", value: 0, reason: "" };
  const seedItems = source.custom_items || [];

  const [discountType, setDiscountType] = useState(seedDiscount.type || "none");
  const [discountValue, setDiscountValue] = useState(String(seedDiscount.value ?? ""));
  const [discountReason, setDiscountReason] = useState(seedDiscount.reason || "");
  const [items, setItems] = useState(seedItems.map((i) => ({
    id: i.id, description: i.description || "", amount: String(i.amount ?? ""), taxable: !!i.taxable,
  })));
  const [savingAdj, setSavingAdj] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);

  // Live-preview totals from the current form state (edit mode) or
  // authoritative revised_totals from the frozen snapshot (locked).
  const preview = useMemo(() => {
    if (isFinalized || isConverted) {
      return source.revised_totals || computeRevisedTotals(snapshot, null, []);
    }
    const cleanItems = items
      .filter((i) => (i.description || "").trim() && Number(i.amount) >= 0)
      .map((i) => ({ description: i.description, amount: Number(i.amount || 0), taxable: !!i.taxable }));
    return computeRevisedTotals(
      snapshot,
      { type: discountType, value: Number(discountValue || 0) },
      cleanItems,
    );
  }, [isFinalized, isConverted, source.revised_totals, snapshot, items, discountType, discountValue]);

  const addItem = () => setItems((xs) => [...xs, { description: "", amount: "", taxable: false }]);
  const removeItem = (idx) => setItems((xs) => xs.filter((_, i) => i !== idx));
  const patchItem = (idx, patch) => setItems((xs) => xs.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const validate = () => {
    if (discountType !== "none") {
      const v = Number(discountValue);
      if (!(v >= 0)) return "Discount value must be a non-negative number.";
      if (discountType === "percentage" && v > 100) return "Percentage discount cannot exceed 100.";
      if (discountType === "fixed" && v > merchSubtotal + 1e-6) {
        return `Fixed discount cannot exceed merch subtotal (${fmt(merchSubtotal)}).`;
      }
    }
    for (const [i, it] of items.entries()) {
      if (!(it.description || "").trim()) return `Custom item #${i + 1}: description required.`;
      const amt = Number(it.amount);
      if (!(amt >= 0) || Number.isNaN(amt)) return `Custom item #${i + 1}: amount must be a non-negative number.`;
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); toast.error(err); return; }
    setError(null); setSavingAdj(true);
    try {
      await api.patch(
        `/admin/quotes/${encodeURIComponent(quote.public_id)}/adjustments`,
        {
          discount: {
            type: discountType,
            value: discountType === "none" ? 0 : Number(discountValue || 0),
            reason: discountReason.trim() || null,
          },
          custom_items: items
            .filter((i) => (i.description || "").trim())
            .map((i) => ({
              description: i.description.trim(),
              amount: Number(i.amount || 0),
              taxable: !!i.taxable,
              ...(i.id ? { id: i.id } : {}),
            })),
        },
      );
      toast.success("Adjustments saved.");
      onChanged?.();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg); toast.error(msg);
    } finally { setSavingAdj(false); }
  };

  const finalize = async () => {
    if (!window.confirm(
      "Finalize this quote? The discount + custom items will be frozen and the quote will be excluded from recovery emails. You can reopen it before converting."
    )) return;
    setFinalizing(true);
    try {
      await api.post(`/admin/quotes/${encodeURIComponent(quote.public_id)}/finalize`);
      toast.success("Quote finalized.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setFinalizing(false); }
  };

  const reopen = async () => {
    if (!window.confirm("Reopen this finalized quote for further edits?")) return;
    setReopening(true);
    try {
      await api.post(`/admin/quotes/${encodeURIComponent(quote.public_id)}/reopen`);
      toast.success("Quote reopened.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setReopening(false); }
  };

  const convert = async () => {
    if (!window.confirm(
      "Convert this finalized quote into an order? A new order will be created linked back to this quote. This action is idempotent — clicking twice will not create duplicates."
    )) return;
    setConverting(true);
    try {
      const r = await api.post(`/admin/quotes/${encodeURIComponent(quote.public_id)}/adjustments/convert`);
      const oid = r.data?.order?.public_id;
      toast.success(r.data?.already_converted
        ? `Already converted to order ${oid}.`
        : `Order ${oid} created.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setConverting(false); }
  };

  return (
    <div
      className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 mb-5"
      data-testid="quote-adjustments-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)]">
          Adjustments · Discount + Custom Items
        </div>
        <StatusPill isConverted={isConverted} isFinalized={isFinalized} />
      </div>

      {isConverted && (
        <div
          className="mb-3 text-sm bg-emerald-50 border border-emerald-800 text-emerald-900 rounded-lg px-3 py-2"
          data-testid="adj-converted-banner"
        >
          Converted to order{" "}
          <Link
            to={`/admin/fulfillment/${convertedOrderId}`}
            className="font-mono font-bold underline"
            data-testid="adj-converted-order-link"
          >
            {convertedOrderId}
          </Link>
          . Adjustments are read-only.
        </div>
      )}

      {isFinalized && !isConverted && (
        <div
          className="mb-3 text-sm bg-amber-50 border border-amber-800 text-amber-900 rounded-lg px-3 py-2"
          data-testid="adj-finalized-banner"
        >
          Quote is finalized. Reopen to edit, or Convert to create an order.
        </div>
      )}

      {/* ── Discount ────────────────────────────────────────────── */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-2">
          Discount
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[140px_140px_1fr] gap-2">
          <select
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value)}
            disabled={!editable}
            className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm bg-white disabled:opacity-60"
            data-testid="adj-discount-type"
          >
            <option value="none">None</option>
            <option value="percentage">Percentage %</option>
            <option value="fixed">Fixed $</option>
          </select>
          <input
            type="text"
            inputMode="decimal"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            disabled={!editable || discountType === "none"}
            placeholder={discountType === "percentage" ? "e.g. 10" : "e.g. 15.00"}
            className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm text-right disabled:opacity-60"
            data-testid="adj-discount-value"
          />
          <input
            type="text"
            value={discountReason}
            onChange={(e) => setDiscountReason(e.target.value)}
            disabled={!editable || discountType === "none"}
            maxLength={200}
            placeholder="Reason (optional, shown on invoice)"
            className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm disabled:opacity-60"
            data-testid="adj-discount-reason"
          />
        </div>
      </div>

      {/* ── Custom items ────────────────────────────────────────── */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
            Custom items ({items.length})
          </div>
          {editable && (
            <button
              type="button"
              onClick={addItem}
              className="text-xs px-3 py-1 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider inline-flex items-center gap-1"
              data-testid="adj-add-item"
            >
              <Plus size={12} /> Add item
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="text-xs text-[var(--smoke)] italic" data-testid="adj-items-empty">
            No custom items — add shipping, rush handling, or a hardcover fee.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[1fr_100px_auto_auto] gap-2 items-center"
                data-testid={`adj-item-row-${idx}`}
              >
                <input
                  type="text"
                  value={it.description}
                  onChange={(e) => patchItem(idx, { description: e.target.value })}
                  disabled={!editable}
                  placeholder="Description (e.g. Rush handling)"
                  className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm disabled:opacity-60"
                  data-testid={`adj-item-desc-${idx}`}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={it.amount}
                  onChange={(e) => patchItem(idx, { amount: e.target.value })}
                  disabled={!editable}
                  placeholder="$ 0.00"
                  className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm text-right disabled:opacity-60"
                  data-testid={`adj-item-amount-${idx}`}
                />
                <label className="text-xs inline-flex items-center gap-1.5 select-none">
                  <input
                    type="checkbox"
                    checked={!!it.taxable}
                    onChange={(e) => patchItem(idx, { taxable: e.target.checked })}
                    disabled={!editable}
                    data-testid={`adj-item-taxable-${idx}`}
                  />
                  Taxable
                </label>
                {editable && (
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="text-red-700 hover:text-red-900 p-1"
                    aria-label="Remove item"
                    data-testid={`adj-item-remove-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Live totals ─────────────────────────────────────────── */}
      <div
        className="bg-neutral-50 border-2 border-[var(--ink)] rounded-xl p-4 mb-4"
        data-testid="adj-totals-preview"
      >
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-2">
          {isFinalized || isConverted ? "Frozen totals" : "Live totals preview"}
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-y-0.5 text-sm">
          <Row label="Merchandise subtotal" value={fmt(preview.merch_subtotal)} testid="adj-total-merch" />
          {preview.discount_amount > 0 && (
            <Row
              label={
                discountType === "percentage"
                  ? `Admin Discount (${Number(discountValue || 0)}%)`
                  : "Admin Discount"
              }
              value={`− ${fmt(preview.discount_amount)}`}
              negative
              testid="adj-total-discount"
            />
          )}
          {preview.discount_amount > 0 && (
            <Row
              label="Discounted merchandise subtotal"
              value={fmt(preview.merch_after_discount)}
              testid="adj-total-merch-after-discount"
            />
          )}
          {preview.custom_taxable_total > 0 && (
            <Row label="Custom items (taxable)" value={fmt(preview.custom_taxable_total)} testid="adj-total-custom-taxable" />
          )}
          {preview.custom_nontaxable_total > 0 && (
            <Row label="Custom items (non-taxable)" value={fmt(preview.custom_nontaxable_total)} testid="adj-total-custom-nontaxable" />
          )}
          <Row label={`Tax (${(taxRate * 100).toFixed(2)}%)`} value={fmt(preview.tax_amount)} testid="adj-total-tax" />
          <Row label="Final total" value={fmt(preview.final_total)} bold testid="adj-total-final" />
        </dl>
      </div>

      {error && (
        <div
          className="mb-3 text-xs bg-red-50 border border-red-800 text-red-900 rounded px-2 py-1.5"
          data-testid="adj-error"
        >
          {error}
        </div>
      )}

      {/* ── Actions ────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {editable && (
          <>
            <button
              type="button"
              onClick={save}
              disabled={savingAdj}
              className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] hover:brightness-110 font-bold uppercase tracking-wider disabled:opacity-50"
              data-testid="adj-save-btn"
            >
              {savingAdj ? <Loader2 size={12} className="animate-spin inline" /> : "Save adjustments"}
            </button>
            <button
              type="button"
              onClick={finalize}
              disabled={finalizing}
              className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--ink)] text-white hover:brightness-125 font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="adj-finalize-btn"
            >
              <Lock size={12} /> {finalizing ? "Finalizing…" : "Finalize quote"}
            </button>
          </>
        )}
        {isFinalized && !isConverted && (
          <>
            <button
              type="button"
              onClick={reopen}
              disabled={reopening}
              className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="adj-reopen-btn"
            >
              <Unlock size={12} /> {reopening ? "Reopening…" : "Reopen"}
            </button>
            <button
              type="button"
              onClick={convert}
              disabled={converting}
              className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white hover:brightness-110 font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="adj-convert-btn"
            >
              <CheckCircle2 size={12} /> {converting ? "Converting…" : "Convert to order"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}


function Row({ label, value, bold, negative, testid }) {
  return (
    <>
      <dt className={"text-[var(--smoke)] " + (bold ? "font-bold text-[var(--ink)]" : "")}>{label}</dt>
      <dd
        className={"font-mono text-right " + (bold ? "font-bold text-[var(--ink)]" : "") + (negative ? " text-red-800" : "")}
        data-testid={testid}
      >
        {value}
      </dd>
    </>
  );
}


function StatusPill({ isConverted, isFinalized }) {
  if (isConverted) {
    return (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-800 text-emerald-900 font-bold uppercase tracking-widest"
        data-testid="adj-status-pill"
      >
        Converted
      </span>
    );
  }
  if (isFinalized) {
    return (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 border border-amber-800 text-amber-900 font-bold uppercase tracking-widest"
        data-testid="adj-status-pill"
      >
        Finalized
      </span>
    );
  }
  return (
    <span
      className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-600 text-neutral-800 font-bold uppercase tracking-widest"
      data-testid="adj-status-pill"
    >
      Draft
    </span>
  );
}
